import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Upload, User } from 'lucide-react';

const ProfileForm = ({
  profileData,
  setProfileData,
  uploadingImage,
  profileSaved,
  loading,
  handleProfileImageUpload,
  handleProfileSubmit,
  getImageUrl
}) => {
  return (
    <form onSubmit={handleProfileSubmit} className="space-y-6">
      {/* Profile Photo */}
      <div className="flex items-center space-x-4">
        <div 
          className="relative cursor-pointer" 
          onClick={() => document.getElementById('profile-photo').click()}
          title="Click to change photo"
        >
          {profileData.profile_photo ? (
            <img
              src={getImageUrl(profileData.profile_photo)}
              alt="Profile"
              className="w-24 h-24 rounded-full object-cover border-4 border-emerald-100 hover:border-emerald-200 transition-colors"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div
            className={`w-24 h-24 rounded-full bg-gray-200 items-center justify-center hover:bg-gray-300 transition-colors ${profileData.profile_photo ? 'hidden' : 'flex'}`}
          >
            <User className="h-12 w-12 text-gray-400" />
          </div>
          <div className="absolute bottom-0 right-0 bg-emerald-600 rounded-full p-2 shadow-lg">
            <Upload className="h-3 w-3 text-white" />
          </div>
        </div>
        <div>
          <Label htmlFor="profile-photo" className="cursor-pointer">
            <div className="flex items-center space-x-2 text-sm text-emerald-600 hover:text-emerald-700">
              <Upload className="h-4 w-4" />
              <span>{uploadingImage ? 'Uploading...' : 'Change Photo'}</span>
            </div>
          </Label>
          <input
            id="profile-photo"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleProfileImageUpload}
            disabled={uploadingImage}
          />
          <p className="text-xs text-gray-500 mt-1">Click photo or button to change</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="display_name">Display Name *</Label>
          <Input
            id="display_name"
            name="display_name"
            value={profileData.display_name}
            onChange={(e) => setProfileData({ ...profileData, display_name: e.target.value })}
            required
            placeholder="How you want to appear in reviews"
          />
        </div>

        <div>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={profileData.phone}
            onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
            placeholder="(555) 123-4567"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="first_name">First Name</Label>
          <Input
            id="first_name"
            name="first_name"
            value={profileData.first_name}
            onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
            placeholder="John"
          />
        </div>

        <div>
          <Label htmlFor="last_name">Last Name</Label>
          <Input
            id="last_name"
            name="last_name"
            value={profileData.last_name}
            onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
            placeholder="Doe"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            name="city"
            value={profileData.city}
            onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
            placeholder="Los Angeles"
          />
        </div>

        <div>
          <Label htmlFor="state">State</Label>
          <Input
            id="state"
            name="state"
            value={profileData.state}
            onChange={(e) => setProfileData({ ...profileData, state: e.target.value })}
            placeholder="CA"
          />
        </div>
      </div>

      <Button 
        type="submit" 
        disabled={loading || !profileData.display_name}
        fullWidth="mobile"
        className={`transition-all duration-300 ${
          profileSaved 
            ? 'bg-green-600 hover:bg-green-700' 
            : ''
        }`}
      >
        {loading ? (
          <span className="flex items-center">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Saving...
          </span>
        ) : profileSaved ? (
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Saved!
          </span>
        ) : (
          'Save Profile'
        )}
      </Button>
    </form>
  );
};

export default ProfileForm;
